import React, { useEffect, useRef, useState } from 'react';
import { ExpandOutlined } from '@ant-design/icons';

import { Bar } from '@nivo/bar';
import { useTranslation } from 'react-i18next';

import './../vignette.style.scss';
import './histogram.style.scss';
import { theme } from '../globalTheme';
import {
  percentReduce,
  getUnitConversion,
  getFormattedWeightNumber,
  PROJECTS_TYPE_NAMES_TOTAL_IMPACT,
  handleResize,
} from '../vignette.utils';

import CustomTooltip from './CustomTooltip';
import { Tooltip } from 'antd';
import { FormatedGraphData } from '../../PdfBuilder/HistogramWithLegend';
import { ComputedProject } from '@photocarbone/nooco-shared-api-def';

const colorsByDefault = [
  '#003c51',
  '#31baae',
  '#a7eade',
  '#038c8c',
  '#015364',
  '#026e78',
  '#159880',
  '#476d66',
  '#4f8b81',
  '#5ab9a8',
  '#8ec2b9',
  '#0ad1ae',
  '#5bdcc6',
];

export interface HistogramVignetteComponentProps {
  data: FormatedGraphData[];
  keys: string[];
  currentProject: ComputedProject;
  total: number;
  isZoom?: boolean;
  isCustomProject?: boolean;
  indexBy?: string;
  isPdf?: boolean;
  acvComponent?: boolean;
  colors?: string[];
  isBackgroundVisible?: boolean;
  isLegendVisible?: boolean;
  pdfTitle?: string;
  i18nInstance?: any;
  onClickFullscreen?: (event?: any) => void;
  onClickBar?: (event?: any) => void;
}

export const HistogramVignetteComponent = ({
  data,
  keys,
  currentProject,
  total,
  isZoom,
  isCustomProject,
  indexBy = 'name',
  isPdf,
  colors = colorsByDefault,
  acvComponent = false,
  isBackgroundVisible = false,
  isLegendVisible = false,
  i18nInstance = null,
  pdfTitle,
  onClickFullscreen,
  onClickBar,
  ...rest
}: HistogramVignetteComponentProps) => {
  // const [isModalOpen, setIsModalOpen] = React.useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const { t: defaultT } = useTranslation();
  const t = i18nInstance ? i18nInstance.getFixedT() : defaultT;

  useEffect(() => {
    handleResize(parentRef, setDimensions);

    window.addEventListener('resize', () => handleResize(parentRef, setDimensions));
    return () =>
      window.removeEventListener('resize', () =>
        handleResize(parentRef, setDimensions)
      );
  }, [parentRef.current]);

  const getImpactText = (currentProject, data, t, getUnitConversion) => {
    const findHighestValueInGraphData = Object.values(data[0]).find(
      (value) => typeof value === 'number'
    );

    const unitConversion = getUnitConversion(findHighestValueInGraphData);

    const refUnitText = currentProject?.refUnit === 'm²' ? '/m²' : '';

    return `${t('APP.REPORT.RESPONSIVEBAR.PH_F798C8', {
      defaultValue: 'Impact environnemental',
    })} (${unitConversion}${refUnitText})`;
  };

  return (
    <div style={isPdf ? { pageBreakInside: 'avoid' } : { height: '100%' }}>
      <h2>{t(pdfTitle)}</h2>
      <div
        className="responsive-bar vignette"
        style={{ backgroundColor: isBackgroundVisible ? '#f3f7ff' : 'transparent' }}
      >
        {/* <ModalHistogram isZoom {...{ data, keys, isModalOpen, setIsModalOpen }} /> */}
        <div
          className="responsive-bar__wrapper"
          ref={!isPdf ? parentRef : undefined}
        >
          {isLegendVisible && acvComponent && (
            <div className={'responsive-bar__acv-wrapper--items'}>
              {keys.map((key, index) => (
                <div className="responsive-bar__acv-item">
                  <div
                    style={{
                      background: colors[index],
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      marginRight: '5px',
                    }}
                  />
                  {key.length > 18 ? `${key.substring(0, 18)}...` : key}
                </div>
              ))}
            </div>
          )}
          <Bar
            enableLabel={false}
            groupMode={acvComponent ? 'grouped' : 'stacked'}
            onClick={onClickBar}
            width={!isPdf ? dimensions.width : 700}
            height={!isPdf ? dimensions.height : 400}
            animate={!isPdf}
            isInteractive={!isPdf}
            theme={theme}
            data={data}
            keys={keys}
            indexBy={indexBy}
            tooltip={(info) => (
              <CustomTooltip
                {...{ total, info, acvComponent }}
                refUnit={currentProject?.refUnit}
              />
            )}
            valueScale={{ type: 'linear' }}
            padding={acvComponent ? 0.5 : 0.6}
            innerPadding={
              acvComponent && keys.length <= 2 ? 10 : acvComponent ? 3 : 0
            }
            margin={{
              top: 30,
              bottom: isPdf ? 90 : isCustomProject ? 40 : 90,
              right: 40,
              left: 85,
            }}
            colors={!colors ? ({ id, data }) => data[`${id}Color`] : colors}
            axisLeft={{
              tickSize: 5,
              tickPadding: 10,
              tickRotation: 0,
              format: (value) =>
                value.toString().length >= 5
                  ? `${getFormattedWeightNumber(value)} ${getUnitConversion(
                      value,
                      true
                    )}`
                  : value,
              legend: getImpactText(currentProject, data, t, getUnitConversion),
              legendPosition: 'middle',
              legendOffset: -70,
            }}
            axisRight={
              !acvComponent
                ? {
                    format: (value) =>
                      `${Math.round((value * 100) / percentReduce(data))}%`,
                  }
                : undefined
            }
            axisBottom={{
              renderTick: (tick) => {
                let value = tick.value;
                let transform = `translate(${tick.x - 30},${tick.y + 35})`;
                let lotValue;
                if (isCustomProject) {
                  lotValue = tick.value.split(' ')[0];
                  transform = `translate(${tick.x},${tick.y + 10})`;
                }

                return (
                  <g transform={transform}>
                    <text
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        ...theme.axis.ticks.text,
                        fill: '#333',
                        transform: isCustomProject ? undefined : 'rotate(-45deg)',
                      }}
                      dx={isCustomProject ? undefined : '-10'}
                    >
                      {value.length > 18 || isCustomProject ? (
                        <Tooltip title={value} placement="bottom">
                          <tspan>
                            {isCustomProject
                              ? lotValue
                              : `${value.substring(0, 18)}...`}
                          </tspan>
                        </Tooltip>
                      ) : (
                        value
                      )}
                    </text>
                  </g>
                );
                // if (isCustomProject) {
                //   return (
                //     <tspan>
                //       <>
                //         <tspan x="0" dy="-0.4em">
                //           {value === 'Autres' ? '[...]' : value.split(' ')[0]}
                //         </tspan>
                //       </>
                //       <title>{value}</title>
                //     </tspan>
                //   );
                // }
              },
            }}
            {...rest}
          />
        </div>
        {!isZoom && !isPdf && (
          <ExpandOutlined
            className="responsive-bar__expand-button"
            onClick={onClickFullscreen}
          />
        )}
      </div>
    </div>
  );
};
